import { useState, useEffect } from 'react';
import { Navigation } from './components/Navigation';
import { HeroSection } from './components/HeroSection';
import { AboutSection } from './components/AboutSection';
import { CultureSection } from './components/CultureSection';
import { ServicesSection } from './components/ServicesSection';
import { ClientsSection } from './components/ClientsSection';
import { TeamSection } from './components/TeamSection';
import { ContactSection } from './components/ContactSection';
import { ParticleAnimation, DigitalRain } from './components/ParticleAnimation';
import { Toaster } from './components/ui/sonner';
import digitechLogo from '/logo.png';
import { ProductSection } from './components/ProductSection';

// Simple hook for localStorage with safe fallback
function useLocalStorage(key: string, defaultValue: boolean) {
  const [value, setValue] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
      }
      return defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setStoredValue = (newValue: boolean) => {
    try {
      setValue(newValue);
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch {
      // Handle storage error silently
      setValue(newValue);
    }
  };

  return [value, setStoredValue] as const;
}

export default function App() {
  // Use particles by default, allow user to toggle
  const [showParticles, setShowParticles] = useLocalStorage('showParticles', true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center relative overflow-hidden">
        <ParticleAnimation 
          className="opacity-40"
          particleCount={60}
          colors={['#FFD700', '#00FFFF', '#00AEEF']}
          speed={1}
        />
        
        <div className="flex flex-col items-center text-center relative z-10">
          <div className="w-32 h-32 rounded-full flex items-center justify-center tech-glow animate-pulse-glow p-6 mb-8 bg-gradient-to-br from-primary/10 to-accent/5">
            <img 
              src={digitechLogo} 
              alt="DigiTech Logo" 
              className="w-full h-full object-contain object-center drop-shadow-2xl"
            />
          </div>
          
          <h1 className="font-orbitron text-4xl font-bold text-primary mb-4 animate-float">
            DI<span className="text-accent-2">ROI</span>
          </h1>
          
          <div className="flex items-center justify-center space-x-1 mb-4">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-accent rounded-full animate-bounce delay-100"></div>
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce delay-200"></div>
          </div>
          
          <p className="text-muted-foreground text-sm">Loading digital innovation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      {/* Global Particle Background */}
      {showParticles && (
        <div className="fixed inset-0 pointer-events-none z-0">
          <ParticleAnimation 
            className="opacity-20"
            particleCount={30}
            colors={['#FFD700', '#00FFFF', '#00AEEF']}
            speed={0.3}
            connectionDistance={80}
            showConnections={true}
            particleTypes={['dot']}
          />
        </div>
      )}
      
      {/* Digital Rain Effect (subtle) */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-10">
        <DigitalRain />
      </div>
      
      {/* Main Content */}
      <div className="relative z-10">
        {/* Navigation */}
        <Navigation />
        
        {/* Hero Section */}
        <HeroSection />
        
        {/* About Section */}
        <AboutSection />
        
        {/* Culture & Values Section */}
        <CultureSection />
        
        {/* Services Section */}
        <ServicesSection />
        {/* Product Section */}
        <ProductSection />
        {/* Clients Section */}
        <ClientsSection />
        
        {/* Team Section */}
        <TeamSection />
        
        {/* Contact Section */}
        <ContactSection />
        
        {/* Footer */}
        <footer className="py-12 border-t border-primary/20 bg-card/30 relative">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-accent/10 p-2 tech-glow flex items-center justify-center">
                    <img 
                      src={digitechLogo} 
                      alt="DIGITECH Logo" 
                      className="w-full h-full object-contain object-center drop-shadow-md"
                    />
                  </div>
                  <div className="font-orbitron text-xl font-bold text-primary flex items-center">
                    DI<span className="text-accent-2">ROI</span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Pioneering digital innovation through intelligent technology, 
                  excellence, and unwavering commitment to transforming businesses globally.
                </p>
                
                {/* Particle Toggle */}
                <button
                  onClick={() => setShowParticles(!showParticles)}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center space-x-2"
                >
                  <div className={`w-2 h-2 rounded-full ${showParticles ? 'bg-primary' : 'bg-muted'}`}></div>
                  <span>{showParticles ? 'Disable' : 'Enable'} Particles</span>
                </button>
              </div>
              
              <div>
                <h4 className="font-orbitron font-semibold text-foreground mb-4">Services</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">AI & Machine Learning</a></li>
                  <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Cloud Infrastructure</a></li>
                  <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Mobile Development</a></li>
                  <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Cybersecurity</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-orbitron font-semibold text-foreground mb-4">Company</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#about" className="text-muted-foreground hover:text-primary transition-colors">About Us</a></li>
                  <li><a href="#team" className="text-muted-foreground hover:text-primary transition-colors">Our Team</a></li>
                  <li><a href="#culture" className="text-muted-foreground hover:text-primary transition-colors">Culture</a></li>
                  <li><a href="#contact" className="text-muted-foreground hover:text-primary transition-colors">Careers</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-orbitron font-semibold text-foreground mb-4">Connect</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#contact" className="text-muted-foreground hover:text-primary transition-colors">Contact Us</a></li>
                  <li><a href="#" className="text-muted-foreground hover:text-primary transition-colors">LinkedIn</a></li>
                  <li><a href="#" className="text-muted-foreground hover:text-primary transition-colors">Twitter</a></li>
                  <li><a href="#" className="text-muted-foreground hover:text-primary transition-colors">GitHub</a></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-primary/20 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="text-sm text-muted-foreground mb-4 md:mb-0">
                  © 2024 Diroi. All rights reserved. | Privacy Policy | Terms of Service
                </div>
                <div className="text-sm text-muted-foreground flex items-center space-x-2">
                  <span>Drive the Future</span>
                  <div className="w-1 h-1 bg-primary rounded-full animate-pulse"></div>
                  <span className="text-xs">v2.1.0</span>
                </div>
              </div>
            </div>
          </div>
        </footer>
        
        {/* Floating Action Button */}
        <div className="fixed bottom-8 right-8 z-50 flex flex-col space-y-3">
          {/* Scroll to top */}
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 tech-glow transition-all duration-300 hover:scale-110 flex items-center justify-center font-orbitron font-bold relative overflow-hidden group"
          >
            <span className="relative z-10">↑</span>
            <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-secondary/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </button>
          
          {/* Quick Contact */}
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="w-12 h-12 bg-accent text-accent-foreground rounded-full shadow-lg hover:bg-accent/90 neon-glow transition-all duration-300 hover:scale-110 flex items-center justify-center text-xl relative overflow-hidden group"
          >
            <span className="relative z-10">✉</span>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </button>
        </div>
      </div>
      
      {/* Toast Notifications */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </div>
  );
}